import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { offlineApi, type Timetable, type BoxField } from '../../api/offline.js';
import { Spinner, ErrorBox } from '../../components/ui.js';
import { useUrlUpdate } from '../../lib/hooks.js';

/**
 * 매장 일일 타임테이블.
 *
 * 이 화면은 사무실이 아니라 **매장 바닥에서** 쓰인다 - 직원이 폰이나 매장 PC 에
 * 하루 종일 띄워두고, 시간대가 올 때마다 완료를 누르고 특이사항을 적는다.
 * 그래서 화면 구성을 "지금 할 일 → 그 다음" 순서로 세웠다.
 *
 *   1) 지금 할 일 카드  - 현재 시간대(없으면 다음 미완료)를 크게, 1탭 완료
 *   2) 입고/박스 현황   - 시간에 민감해서 타임라인보다 위(모바일)·옆(데스크톱)
 *   3) 전체 타임라인    - 하루 흐름을 훑고 지난 칸을 채우는 자리
 *
 * 저장은 전부 자동이다(입력이 멈추면 저장, 포커스가 빠지거나 Enter 면 즉시).
 * 예전에는 메모·박스가 저장 버튼 모양이 서로 달랐는데, 매장에서 한 손으로 쓰는
 * 화면에서 탭을 하나라도 줄이는 편이 낫다.
 */

/** 레거시 offline_timetable_input.php 의 업무 문구를 그대로 보존한다. */
const SLOT_TASKS: Record<string, { title: string; note?: string }> = {
  t11001110: { title: '매장 오픈 준비 완료' },
  t11101200: { title: '매장 청소' },
  t12001230: { title: '진열 상태 체크' },
  t12001230f: { title: '전자액자 특이사항 체크' },
  t12301330: { title: '입고 요청' },
  t14301600: { title: '입고 상품 정리', note: '입고 상품이 없으면 진열 상태를 체크합니다.' },
  t16001630: { title: '중간 보고' },
  t17301800: { title: '진열 상태 체크' },
  t22002255: { title: '결과 보고' },
  t22552300: { title: '마감 완료' },
};

/**
 * 박스 항목. quick 은 그 칸에서 제일 자주 쓰는 값이다 - 매장에서 실제로 적히는
 * 값이 "0박스" / "미입고" 처럼 몇 개로 정해져 있어서, 타이핑 대신 한 번 누르게 한다.
 */
const BOX_META: Record<BoxField, { label: string; description: string; placeholder: string; quick: string[] }> = {
  notDoneBox: {
    label: '미정리 박스',
    description: '현재 밀려 있는 박스 수',
    placeholder: '예: 2박스',
    quick: ['0박스', '1박스', '2박스'],
  },
  inboxTime: {
    label: '입고 시간',
    description: '박스가 매장에 도착한 시간',
    placeholder: '예: 14:30',
    quick: ['@now', '미입고'],
  },
  boxCleanup: {
    label: '입고 정리 완료',
    description: '정리를 마친 시간과 수량',
    placeholder: '예: 14:30 (2박스 완료)',
    quick: ['@now', '해당 없음'],
  },
};

interface SlotTime {
  /** 자정 기준 분 */
  start: number;
  end: number;
  label: string;
}

/** 't14301600' → 14:30~16:00. 뒤의 'f' 는 같은 시간대의 두 번째 업무라는 표시다. */
function parseSlot(slot: string): SlotTime | null {
  const m = /^t(\d{2})(\d{2})(\d{2})(\d{2})f?$/.exec(slot);
  if (!m) return null;
  const [, h1, m1, h2, m2] = m;
  return {
    start: Number(h1) * 60 + Number(m1),
    end: Number(h2) * 60 + Number(m2),
    label: `${h1}:${m1}~${h2}:${m2}`,
  };
}

function slotLabel(slot: string): string {
  return parseSlot(slot)?.label ?? slot;
}

/** 브라우저의 현지 날짜를 쓴다. UTC 문자열은 한국 시간 오전에 전날이 된다. */
function localDate(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function moveDate(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return localDate();
  date.setDate(date.getDate() + days);
  return localDate(date);
}

function nowHHMM(date = new Date()): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** 'HH:MM' 만 남긴다 - 완료 시각은 초까지 볼 일이 없다. */
function timeOnly(value: string): string {
  const m = /(\d{2}):(\d{2})/.exec(value ?? '');
  return m ? `${m[1]}:${m[2]}` : '';
}

export function TimetablePage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);

  const meta = useQuery({ queryKey: ['offline', 'meta'], queryFn: () => offlineApi.meta() });
  const place = params.get('place') ?? meta.data?.places[0]?.code ?? 'u';
  const workdate = params.get('date') ?? localDate();

  const tt = useQuery({
    queryKey: ['offline', 'timetable', place, workdate],
    queryFn: () => offlineApi.timetable(place, workdate),
    enabled: Boolean(meta.data),
  });

  const [confirming, setConfirming] = useState<string | null>(null);
  const [justDone, setJustDone] = useState<string | null>(null);

  // '지금'은 1분마다 다시 본다 - 시간대가 넘어가면 히어로 카드가 스스로 다음
  // 업무로 넘어가야 한다(직원이 새로고침할 이유를 만들지 않는다).
  const [minute, setMinute] = useState(() => new Date().getHours() * 60 + new Date().getMinutes());
  useEffect(() => {
    const id = setInterval(() => setMinute(new Date().getHours() * 60 + new Date().getMinutes()), 30_000);
    return () => clearInterval(id);
  }, []);

  const refresh = () => qc.invalidateQueries({ queryKey: ['offline', 'timetable', place, workdate] });

  const start = useMutation({
    mutationFn: (w: { worker1: string; worker2: string }) => offlineApi.start(place, workdate, w.worker1, w.worker2),
    onSuccess: refresh,
  });

  const check = useMutation({
    mutationFn: (slot: string) => offlineApi.checkSlot(place, workdate, slot),
    onSuccess: (_result, slot) => {
      setConfirming(null);
      setJustDone(slot);
      window.setTimeout(() => setJustDone((s) => (s === slot ? null : s)), 2000);
      void refresh();
    },
  });

  if (meta.isPending) return <Spinner />;
  if (meta.isError) return <ErrorBox error={meta.error} onRetry={() => void meta.refetch()} />;

  const places = meta.data.places;
  const placeLabel = places.find((p) => p.code === place)?.label ?? place;
  const slots = meta.data.slots;
  const isToday = workdate === localDate();

  const doneSlots = new Set(
    (tt.data?.items ?? []).filter((i) => i.isdone === 'Y').map((i) => i.slot),
  );
  const doneCount = slots.filter((s) => doneSlots.has(s)).length;

  /**
   * 지금 눌러야 할 업무 하나.
   * 오늘이면 현재 시각이 들어 있는 미완료 슬롯을, 없으면 다음에 올 미완료
   * 슬롯을 고른다. 지난 날짜에는 '지금'이 없으므로 첫 미완료를 그냥 보여준다.
   */
  const pending = slots.filter((s) => !doneSlots.has(s));
  const currentSlot = (() => {
    if (pending.length === 0) return null;
    if (!isToday) return pending[0]!;
    const inRange = pending.find((s) => {
      const t = parseSlot(s);
      return t && minute >= t.start && minute < t.end;
    });
    if (inRange) return inRange;
    const upcoming = pending.find((s) => (parseSlot(s)?.start ?? 0) > minute);
    // 영업시간이 다 지났는데 미완료가 남았으면 가장 이른 것부터 치운다.
    return upcoming ?? pending[0]!;
  })();

  return (
    <section className="tt">
      <TopBar
        places={places}
        place={place}
        placeLabel={placeLabel}
        workdate={workdate}
        isToday={isToday}
        hasData={Boolean(tt.data)}
        doneCount={doneCount}
        totalCount={slots.length}
        onChange={(next) => update(next)}
      />

      {tt.isPending ? (
        <div className="tt-loading">
          <Spinner label={`${placeLabel} 타임테이블을 불러오는 중…`} />
        </div>
      ) : tt.isError ? (
        <ErrorBox error={tt.error} onRetry={() => void tt.refetch()} />
      ) : !tt.data ? (
        <StartCard
          placeLabel={placeLabel}
          workdate={workdate}
          onStart={(worker1, worker2) => start.mutate({ worker1, worker2 })}
          pending={start.isPending}
          error={start.error}
        />
      ) : (
        <>
          {check.isError && <ErrorBox error={check.error} />}

          <FocusCard
            slot={currentSlot}
            isToday={isToday}
            minute={minute}
            workers={[tt.data.worker1, tt.data.worker2].filter(Boolean).join(' · ')}
            remaining={slots.length - doneCount}
            onComplete={(slot) => setConfirming(slot)}
            busy={check.isPending}
          />

          <div className="tt-grid">
            <BoxRail timetable={tt.data} place={place} workdate={workdate} onSaved={refresh} />

            <Timeline
              slots={slots}
              timetable={tt.data}
              currentSlot={currentSlot}
              justDone={justDone}
              place={place}
              workdate={workdate}
              onComplete={(slot) => setConfirming(slot)}
              busySlot={check.isPending ? check.variables : undefined}
              onSaved={refresh}
            />
          </div>
        </>
      )}

      {confirming && (
        <CompleteDialog
          slot={confirming}
          pending={check.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => check.mutate(confirming)}
        />
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   상단 컨텍스트 바 - 매장·날짜·진행률. 스크롤해도 붙어 있어서, 타임라인을
   내려보다가도 지금 어느 매장 어느 날짜를 보고 있는지 잃지 않는다.
   ───────────────────────────────────────────────────────────────────────── */
function TopBar({
  places, place, placeLabel, workdate, isToday, hasData, doneCount, totalCount, onChange,
}: {
  places: { code: string; label: string }[];
  place: string;
  placeLabel: string;
  workdate: string;
  isToday: boolean;
  hasData: boolean;
  doneCount: number;
  totalCount: number;
  onChange: (next: { place?: string; date?: string }) => void;
}) {
  const percent = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <header className="tt-topbar">
      <div className="tt-topbar__title">
        <h1>매장 일일 타임테이블</h1>
        <p>
          {placeLabel} · {workdate}
          {isToday && <span className="tt-chip tt-chip--today">오늘</span>}
        </p>
      </div>

      <div className="tt-topbar__controls">
        <div className="tt-segment" role="group" aria-label="근무 매장">
          {places.map((p) => (
            <button
              key={p.code}
              type="button"
              className={p.code === place ? 'tt-segment__item tt-segment__item--on' : 'tt-segment__item'}
              aria-pressed={p.code === place}
              onClick={() => onChange({ place: p.code })}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="tt-datenav">
          <button type="button" className="tt-iconbtn" aria-label="이전 날짜" onClick={() => onChange({ date: moveDate(workdate, -1) })}>
            ‹
          </button>
          <input
            className="tt-dateinput"
            type="date"
            aria-label="업무 일자"
            value={workdate}
            onChange={(e) => { if (e.target.value) onChange({ date: e.target.value }); }}
          />
          <button type="button" className="tt-iconbtn" aria-label="다음 날짜" onClick={() => onChange({ date: moveDate(workdate, 1) })}>
            ›
          </button>
          <button type="button" className="tt-todaybtn" disabled={isToday} onClick={() => onChange({ date: localDate() })}>
            오늘
          </button>
        </div>

        {hasData && (
          <div
            className="tt-progress"
            role="progressbar"
            aria-label="업무 진행률"
            aria-valuemin={0}
            aria-valuemax={totalCount}
            aria-valuenow={doneCount}
            aria-valuetext={`${totalCount}개 중 ${doneCount}개 완료`}
          >
            <svg viewBox="0 0 36 36" aria-hidden="true">
              <circle className="tt-progress__track" cx="18" cy="18" r="15.5" />
              <circle
                className="tt-progress__value"
                cx="18" cy="18" r="15.5"
                style={{ strokeDasharray: `${percent} 100` }}
              />
            </svg>
            <span>
              <strong>{doneCount}</strong>/{totalCount}
            </span>
          </div>
        )}
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   지금 할 일 - 이 화면에서 제일 많이 누르는 버튼 하나를 화면 맨 위에 크게.
   ───────────────────────────────────────────────────────────────────────── */
function FocusCard({
  slot, isToday, minute, workers, remaining, onComplete, busy,
}: {
  slot: string | null;
  isToday: boolean;
  minute: number;
  workers: string;
  remaining: number;
  onComplete: (slot: string) => void;
  busy: boolean;
}) {
  if (!slot) {
    return (
      <section className="tt-focus tt-focus--clear" aria-label="지금 할 일">
        <div className="tt-focus__badge tt-focus__badge--done" aria-hidden="true">✓</div>
        <div className="tt-focus__body">
          <span className="tt-focus__eyebrow">모두 완료</span>
          <h2>오늘 업무를 전부 마쳤습니다</h2>
          <p>{workers ? `${workers} 근무` : '수고하셨습니다.'}</p>
        </div>
      </section>
    );
  }

  const task = SLOT_TASKS[slot] ?? { title: slot };
  const time = parseSlot(slot);
  const inProgress = isToday && time !== null && minute >= time.start && minute < time.end;
  const startsIn = isToday && time ? time.start - minute : null;

  return (
    <section className={inProgress ? 'tt-focus tt-focus--now' : 'tt-focus'} aria-label="지금 할 일">
      <div className="tt-focus__badge" aria-hidden="true">
        {slotLabel(slot).split('~')[0]}
      </div>

      <div className="tt-focus__body">
        <span className="tt-focus__eyebrow">
          {inProgress ? '지금 진행 중' : startsIn !== null && startsIn > 0 ? `${startsIn}분 뒤 시작` : '다음 업무'}
        </span>
        <h2>{task.title}</h2>
        <p>
          {slotLabel(slot)}
          {task.note ? ` · ${task.note}` : ''}
          {remaining > 1 ? ` · 남은 업무 ${remaining}개` : ''}
        </p>
      </div>

      <button type="button" className="tt-focus__cta" disabled={busy} onClick={() => onComplete(slot)}>
        완료 처리
      </button>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   입고/박스 현황
   ───────────────────────────────────────────────────────────────────────── */
function BoxRail({
  timetable, place, workdate, onSaved,
}: {
  timetable: Timetable; place: string; workdate: string; onSaved: () => void;
}) {
  return (
    <aside className="tt-rail" aria-label="입고 및 박스 현황">
      <div className="tt-rail__head">
        <h2>입고 · 박스 현황</h2>
        <p>해당 사항이 없어도 ‘0박스’·‘미입고’로 남겨주세요.</p>
      </div>
      {(Object.keys(BOX_META) as BoxField[]).map((field) => (
        <BoxField_
          key={`${place}-${workdate}-${field}`}
          field={field}
          place={place}
          workdate={workdate}
          value={timetable[field]}
          onSaved={onSaved}
        />
      ))}
    </aside>
  );
}

function BoxField_({
  field, place, workdate, value, onSaved,
}: {
  field: BoxField; place: string; workdate: string; value: string; onSaved: () => void;
}) {
  const m = BOX_META[field];
  const save = useAutosave({
    value,
    onSave: (next) => offlineApi.setBox(place, workdate, field, next),
    onSaved,
  });

  return (
    <div className="tt-field">
      <label className="tt-field__label" htmlFor={`box-${field}`}>
        <span className="tt-field__name">{m.label}</span>
        <small>{m.description}</small>
      </label>
      <input
        id={`box-${field}`}
        className="input tt-field__input"
        aria-label={field === 'notDoneBox' ? '미처리 박스' : m.label}
        maxLength={255}
        value={save.text}
        placeholder={m.placeholder}
        onChange={(e) => save.setText(e.target.value)}
        onBlur={save.flush}
        onKeyDown={(e) => { if (e.key === 'Enter') save.flush(); }}
      />
      <div className="tt-field__foot">
        <div className="tt-quick">
          {m.quick.map((q) => {
            const label = q === '@now' ? nowHHMM() : q;
            return (
              <button
                key={q}
                type="button"
                className="tt-quick__chip"
                onClick={() => save.setText(label, { immediate: true })}
              >
                {label}
              </button>
            );
          })}
        </div>
        <SaveStatus state={save.state} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   타임라인
   ───────────────────────────────────────────────────────────────────────── */
function Timeline({
  slots, timetable, currentSlot, justDone, place, workdate, onComplete, busySlot, onSaved,
}: {
  slots: string[];
  timetable: Timetable;
  currentSlot: string | null;
  justDone: string | null;
  place: string;
  workdate: string;
  onComplete: (slot: string) => void;
  busySlot?: string;
  onSaved: () => void;
}) {
  const items = new Map(timetable.items.filter((i) => i.isdone === 'Y').map((i) => [i.slot, i]));

  return (
    <section className="tt-timeline" aria-label="시간대별 업무">
      <div className="tt-timeline__head">
        <h2>시간대별 업무</h2>
        <p>완료 시각은 누른 시점으로 기록되며 되돌릴 수 없습니다.</p>
      </div>

      <ol className="tt-slots">
        {slots.map((slot) => {
          const item = items.get(slot);
          const task = SLOT_TASKS[slot] ?? { title: slot };
          const state = item ? 'done' : slot === currentSlot ? 'current' : 'todo';

          return (
            <li
              key={`${place}-${workdate}-${slot}`}
              className={`tt-slot tt-slot--${state}${justDone === slot ? ' tt-slot--flash' : ''}`}
            >
              <div className="tt-slot__rail" aria-hidden="true">
                <i>{item ? '✓' : ''}</i>
              </div>

              <div className="tt-slot__main">
                <div className="tt-slot__head">
                  <time className="tt-slot__time">{slotLabel(slot)}</time>
                  <h3>{task.title}</h3>
                  {state === 'current' && <span className="tt-chip tt-chip--now">지금</span>}
                  {item && <span className="tt-slot__at">완료 {timeOnly(item.at)}</span>}
                </div>

                {task.note && <p className="tt-slot__note">{task.note}</p>}

                {item ? (
                  <SlotMemo place={place} workdate={workdate} slot={slot} value={item.memo} onSaved={onSaved} />
                ) : (
                  <button
                    type="button"
                    className="tt-slot__cta"
                    disabled={Boolean(busySlot)}
                    onClick={() => onComplete(slot)}
                  >
                    {busySlot === slot ? '처리 중…' : '완료 처리'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function SlotMemo({
  place, workdate, slot, value, onSaved,
}: {
  place: string; workdate: string; slot: string; value: string; onSaved: () => void;
}) {
  const save = useAutosave({
    value,
    onSave: (next) => offlineApi.slotMemo(place, workdate, slot, next),
    onSaved,
  });

  return (
    <div className="tt-slot__memo">
      <input
        className="input tt-field__input"
        aria-label={`${slotLabel(slot)} 메모`}
        maxLength={500}
        value={save.text}
        placeholder="특이사항이 있으면 기록하세요"
        onChange={(e) => save.setText(e.target.value)}
        onBlur={save.flush}
        onKeyDown={(e) => { if (e.key === 'Enter') save.flush(); }}
      />
      <SaveStatus state={save.state} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   업무 시작
   ───────────────────────────────────────────────────────────────────────── */
function StartCard({
  placeLabel, workdate, onStart, pending, error,
}: {
  placeLabel: string; workdate: string; onStart: (w1: string, w2: string) => void; pending: boolean; error: unknown;
}) {
  const [worker1, setWorker1] = useState('');
  const [worker2, setWorker2] = useState('');

  return (
    <div className="tt-start">
      <div className="tt-start__intro">
        <span className="tt-focus__eyebrow">업무 준비</span>
        <h2>
          {placeLabel} · {workdate}
          <br />
          업무를 시작합니다
        </h2>
        <p>근무자를 적으면 시간대별 체크리스트가 열립니다. 메인 근무자만 필수입니다.</p>
      </div>

      <form
        className="tt-start__form"
        onSubmit={(e) => { e.preventDefault(); onStart(worker1, worker2); }}
      >
        {error ? <ErrorBox error={error} /> : null}
        <label className="tt-field__label" htmlFor="worker1">
          <span className="tt-field__name">메인 근무자</span>
          <small>필수</small>
        </label>
        <input
          id="worker1"
          className="input tt-field__input"
          autoFocus
          maxLength={60}
          value={worker1}
          placeholder="예: 홍길동"
          onChange={(e) => setWorker1(e.target.value)}
        />

        <label className="tt-field__label" htmlFor="worker2">
          <span className="tt-field__name">서브 근무자</span>
          <small>선택</small>
        </label>
        <input
          id="worker2"
          className="input tt-field__input"
          maxLength={60}
          value={worker2}
          placeholder="예: 임꺽정"
          onChange={(e) => setWorker2(e.target.value)}
        />

        <button className="btn btn--primary tt-start__cta" type="submit" disabled={pending || worker1.trim() === ''}>
          {pending ? '업무를 시작하는 중…' : '오늘 업무 시작'}
        </button>
      </form>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   완료 확인 - 되돌릴 수 없는 동작이라 한 번 더 묻는다. window.confirm 대신
   앱의 다른 확인 창(.modal-panel)과 같은 모양으로 맞췄다.
   ───────────────────────────────────────────────────────────────────────── */
function CompleteDialog({
  slot, pending, onCancel, onConfirm,
}: {
  slot: string; pending: boolean; onCancel: () => void; onConfirm: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const task = SLOT_TASKS[slot] ?? { title: slot };

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-panel tt-dialog" role="dialog" aria-modal="true" aria-labelledby="tt-dialog-title" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onCancel} aria-label="닫기">×</button>
        <p className="tt-dialog__time">{slotLabel(slot)}</p>
        <h2 className="modal-title" id="tt-dialog-title">{task.title}</h2>
        <p className="hint">완료 시각이 지금으로 기록되며, 완료 후에는 취소할 수 없습니다.</p>
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>취소</button>
          <button ref={confirmRef} type="button" className="btn btn--primary" disabled={pending} onClick={onConfirm}>
            {pending ? '처리 중…' : '완료 처리'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   자동 저장
   ───────────────────────────────────────────────────────────────────────── */
type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

function SaveStatus({ state }: { state: SaveState }) {
  // 상태 글자는 화면에 늘 자리를 차지하게 둔다(사라질 때 옆 요소가 안 흔들리게).
  // aria-live 로 읽어주되, 조용한 상태(idle)에는 아무 말도 안 한다.
  const text =
    state === 'saving' ? '저장 중…' : state === 'saved' ? '저장됨' : state === 'error' ? '저장 실패' : '';
  return (
    <span className={`tt-savestate tt-savestate--${state}`} role="status" aria-live="polite">
      {text}
    </span>
  );
}

/**
 * 입력이 멈추면 저장하고, 포커스가 빠지거나 Enter 면 즉시 저장한다.
 *
 * 서버 값을 useEffect 로 되받아 쓰지 않는다 - 같은 타임테이블 쿼리가 다른
 * 이유로(다른 칸 저장 등) 다시 읽힐 때, 아직 저장 안 한 입력이 조용히 지워지는
 * 회귀가 있었다(TimetablePage.test.tsx). draft 는 이 칸이 스스로 저장했을
 * 때만 비운다.
 */
function useAutosave({
  value, onSave, onSaved, delay = 700,
}: {
  value: string;
  onSave: (next: string) => Promise<unknown>;
  onSaved: () => void;
  delay?: number;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [state, setState] = useState<SaveState>('idle');
  const timer = useRef<number | undefined>(undefined);
  const inFlight = useRef<string | null>(null);

  const text = draft ?? value;
  const textRef = useRef(text);
  textRef.current = text;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const commit = useMemo(
    () => async () => {
      const next = textRef.current;
      // 서버에 있는 값과 같거나, 같은 값을 이미 보내는 중이면 다시 안 보낸다.
      if (next === valueRef.current || inFlight.current === next) return;
      inFlight.current = next;
      setState('saving');
      try {
        await onSave(next);
        inFlight.current = null;
        setDraft((d) => (d === next ? null : d));
        setState('saved');
        onSaved();
        window.setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 1800);
      } catch {
        inFlight.current = null;
        setState('error');
      }
    },
    [onSave, onSaved],
  );

  function setText(next: string, opts?: { immediate?: boolean }) {
    setDraft(next);
    setState('dirty');
    window.clearTimeout(timer.current);
    textRef.current = next;
    if (opts?.immediate) void commit();
    else timer.current = window.setTimeout(() => void commit(), delay);
  }

  function flush() {
    window.clearTimeout(timer.current);
    void commit();
  }

  return { text, setText, flush, state };
}
